// write by cursor
// 生成时间: 2025-07-29 19:45:14
// 简化的请求管理器配置 - 最小化业务代码改动
//
// 【配置变更说明】
// 1. 新增 disableGlobalErrorHandle 全局错误处理控制参数
// 2. 新增 enableErrorHandle 单次请求错误处理启用参数
// 3. 保持 skipErrorHandle 单次请求错误处理跳过参数
// 4. 错误处理优先级：skipErrorHandle > enableErrorHandle > disableGlobalErrorHandle
//
// 【使用建议】
// - 用户界面应用：建议设置 disableGlobalErrorHandle: false（系统自动处理错误）
// - 后台任务/批量处理：建议设置 disableGlobalErrorHandle: true（业务代码精确控制）
// - 当前项目配置：disableGlobalErrorHandle: true（全局禁用，接口主动开启）

import { createRequestManager } from "../gs-utils/core/request";
import type { ApiRequestConfig } from "../gs-utils/core/request";
import { envConfig } from "../envConfig";
import { ERROR_CODE_MAP } from "../constants/errorCode";

/**
 * 创建请求管理器实例 - write by cursor
 *
 * 错误处理配置说明：
 * 1. disableGlobalErrorHandle: false（默认）- 全局启用错误处理
 *    - 所有接口默认使用错误处理链处理错误
 *    - 单个接口可通过 skipErrorHandle: true 跳过错误处理
 *
 * 2. disableGlobalErrorHandle: true - 全局禁用错误处理
 *    - 所有接口默认跳过错误处理，直接抛出错误
 *    - 单个接口可通过 enableErrorHandle: true 启用错误处理
 *
 * 优先级：skipErrorHandle > enableErrorHandle > disableGlobalErrorHandle
 *
 * 使用示例：
 * ```typescript
 * // 全局禁用错误处理时的接口调用
 *
 * // 1. 默认行为 - 直接抛出错误，业务代码需要try/catch处理
 * try {
 *   const data = await get('/api/users');
 * } catch (error) {
 *   // 处理错误
 * }
 *
 * // 2. 单个接口启用错误处理 - 使用系统错误处理链
 * const data = await get('/api/users', { enableErrorHandle: true });
 *
 * // 3. 强制跳过错误处理（即使全局启用）
 * try {
 *   const data = await get('/api/users', { skipErrorHandle: true });
 * } catch (error) {
 *   // 处理错误
 * }
 * ```
 */
const requestInstance = createRequestManager({
  defaultDomain: envConfig.apiGetway,
  errorCodeMap: ERROR_CODE_MAP,
  disableGlobalErrorHandle: true, // 全局禁用错误处理，接口需要主动开启
});

// 导出请求方法 - 与原有API保持完全兼容
export const get = requestInstance.get;
export const post = requestInstance.post;
export const put = requestInstance.put;
export const patch = requestInstance.patch;
export const request = requestInstance.request;
export const del = requestInstance.delete;

// 兼容旧的导出名称
export { del as delete };

// 导出错误处理工具
export const errorHandler = requestInstance.errorHandler;

// 导出端点管理器
export const endpointManager = requestInstance.endpointManager;

// 导出默认实例
export default requestInstance;

// 导出类型
export type { ApiRequestConfig };
